import { usePrivateToast } from "@/shared/usePrivateToast";
import { apiFetch, inspectSession } from "@/lib/api/client";
import { capturePrivateSession, assertPrivateSessionLifetime } from "@/lib/api/private-session";
import { toast as currentToast } from "@a2z/ui";
import { openPrivateRestaurantNotification } from "./notificationLifetime";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";


import { endpoints } from "@/lib/api/endpoints";
import { subscribeToRestaurantForegroundMessages } from "@/lib/firebase/messaging";
import { RESTAURANT_NOTIFICATION_TYPES } from "@/lib/firebase/notification-types";
import { useI18n, useTranslate } from "@/lib/i18n";
import { useSession } from "@/modules/auth/useSession";
import {
  invalidateRestaurantNotificationQueries,
  parseRestaurantNotificationData,
  type RestaurantNotificationData,
} from "./notificationBehavior";
import { restaurantNotificationCopy } from "./notificationCopy";
import { useOptionalRestaurantNotifications } from "./NotificationsProvider";
import {
  subscribeToRestaurantNotificationClicks,
  type RestaurantNotificationClickMessage,
} from "./notificationClickBridge";

export function useOpenRestaurantNotification() {
  const [lifetime] = useState(capturePrivateSession);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { locale } = useI18n();
  const t = useTranslate();
  const { data: session } = useSession();

  return useCallback(
    async (notification: RestaurantNotificationData): Promise<boolean> => {
      assertPrivateSessionLifetime(lifetime);
      if (!session) return false;
      return openPrivateRestaurantNotification({
        session,
        notification,
        locale,
        queryClient,
        switchStore: (storeSlug) =>
          apiFetch<void>(endpoints.seller.switchStore, {
            method: "POST",
            body: { storeSlug },
          }),
        refreshSession: inspectSession,
        recoverBinding: () => navigate(`/${locale}`, { replace: true }),
        navigate,
        onUnavailable: () =>
          currentToast.error(
            t((messages) => messages.notifications.storeUnavailableTitle),
            t((messages) => messages.notifications.storeUnavailableBody),
          ),
        onFailure: () =>
          currentToast.error(
            t((messages) => messages.notifications.openFailedTitle),
            t((messages) => messages.notifications.openFailedBody),
          ),
      }, lifetime);
    },
    [lifetime, locale, navigate, queryClient, session, t],
  );
}

type ForegroundSubscriber = (
  listener: (payload: { data?: Record<string, string> }) => void,
) => () => void;

type ClickSubscriber = (
  listener: (message: RestaurantNotificationClickMessage) => void,
) => () => void;

export function RestaurantNotificationsRuntime({
  subscribeForeground = subscribeToRestaurantForegroundMessages,
  subscribeClicks = subscribeToRestaurantNotificationClicks,
}: {
  subscribeForeground?: ForegroundSubscriber;
  subscribeClicks?: ClickSubscriber;
}) {
  const toast = usePrivateToast();
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const t = useTranslate();
  const openNotification = useOpenRestaurantNotification();
  const notifications = useOptionalRestaurantNotifications();
  const notifySound = notifications?.notifySound;
  const startSoundReminder = notifications?.startSoundReminder;

  useEffect(
    () =>
      subscribeForeground((payload) => {
        const raw = payload.data ?? {};
        const notification = parseRestaurantNotificationData(raw);
        if (!notification) return;
        notifySound?.(raw);
        if (notification.storeId === session?.seller.storeId) {
          void invalidateRestaurantNotificationQueries(
            queryClient,
            notification,
          );
        }
        const copy = restaurantNotificationCopy(
          {
            type: notification.type,
            title: raw.title ?? "",
            body: raw.body ?? "",
            data: notification,
          },
          t,
        );
        toast.info(copy.title, copy.body, {
          dedupeKey: notification.type === RESTAURANT_NOTIFICATION_TYPES.kitchenTicketsCreated
            ? `restaurant-kitchen:${notification.storeId}:${notification.invoiceId}` : undefined,
          durationMs: null,
          onDismiss: notification.storeId === session?.seller.storeId &&
            notification.type !== RESTAURANT_NOTIFICATION_TYPES.dayClosed &&
            notification.type !== RESTAURANT_NOTIFICATION_TYPES.approvalUsed
              ? startSoundReminder?.() : undefined,
          onClick: () => void openNotification(notification),
        });
      }),
    [notifySound, startSoundReminder, openNotification, queryClient, session?.seller.storeId, subscribeForeground, t, toast],
  );

  useEffect(
    () =>
      subscribeClicks((message) => {
        const notification = parseRestaurantNotificationData(message.data);
        if (notification) void openNotification(notification);
      }),
    [openNotification, subscribeClicks],
  );

  return null;
}
